/**
 * Service for mapping appointment slots to appropriate appointment types
 * based on patient type and slot duration
 */
class AppointmentTypeMapper {
  // Duration-based mapping for appointment types (based on real Athena data)
  static APPOINTMENT_TYPE_MAPPING = {
    new: {
      15: { preferred: 'ANY 15', fallback: 'ANY 20' },
      20: { preferred: 'ANY 20', fallback: 'ANY 30' },
      30: { preferred: 'NEW PATIENT 30', fallback: 'ANY 30' },
      40: { preferred: 'NEW PATIENT 40', fallback: 'ANY 30' },
      60: { preferred: 'ANY 30', fallback: 'TELEHEALTH' }
    },
    existing: {
      15: { preferred: 'FOLLOW UP 15', fallback: 'ANY 15' },
      20: { preferred: ['FOLLOW UP 20', 'Follow Up 20'], fallback: 'ANY 20' },
      30: { preferred: 'FOLLOW UP 30', fallback: ['ANY 30', 'TELEHEALTH'] },
      40: { preferred: 'ANY 30', fallback: 'TELEHEALTH' },
      60: { preferred: 'TELEHEALTH', fallback: 'ANY 30' }
    }
  };

  /**
   * Find the best appointment type match for a given duration and patient type
   * @param {number} duration - Slot duration in minutes
   * @param {string} patientType - 'new' or 'existing'
   * @param {Array} appointmentTypes - Available appointment types
   * @returns {Object} Best matching appointment type
   */
  static findBestMatch(duration, patientType, appointmentTypes) {
    if (!appointmentTypes || appointmentTypes.length === 0) {
      console.warn('No appointment types available for mapping');
      return null;
    }

    // DEBUG: Show all available appointment types first
    console.log(`📋 ALL Available appointment types (${appointmentTypes.length}):`, 
      appointmentTypes.map(t => `"${t.name}" (ID: ${t.appointmenttypeid}, ${t.duration}min)`));

    // CRITICAL FIX: Filter appointment types by patient type FIRST
    let filteredTypes = [];
    
    if (patientType === 'new') {
      // For new patients: NEW PATIENT types + ANY types + TELEHEALTH
      filteredTypes = appointmentTypes.filter(type => {
        if (!type.name) return false;
        const nameUpper = type.name.toUpperCase();
        return nameUpper.includes('NEW PATIENT') || 
               nameUpper.includes('ANY') ||
               nameUpper.includes('TELEHEALTH');
      });
    } else if (patientType === 'existing') {
      // For existing patients: FOLLOW UP types + ANY types + TELEHEALTH
      filteredTypes = appointmentTypes.filter(type => {
        if (!type.name) return false;
        const nameUpper = type.name.toUpperCase();
        const hasFollowUp = nameUpper.includes('FOLLOW UP') || nameUpper.includes('FOLLOW-UP');
        const hasAny = nameUpper.includes('ANY');
        const hasTelehealth = nameUpper.includes('TELEHEALTH');
        
        console.log(`🔍 Checking "${type.name}": hasFollowUp=${hasFollowUp}, hasAny=${hasAny}, hasTelehealth=${hasTelehealth}`);
        
        return hasFollowUp || hasAny || hasTelehealth;
      });
    } else {
      filteredTypes = appointmentTypes;
    }

    console.log(`🎯 Patient Type Filter: ${patientType} - Filtered ${appointmentTypes.length} → ${filteredTypes.length} types:`, 
      filteredTypes.map(t => `${t.name} (ID: ${t.appointmenttypeid}, ${t.duration}min)`));

    if (filteredTypes.length === 0) {
      console.warn(`❌ No appropriate appointment types found for ${patientType} patient`);
      return null;
    }

    const mapping = this.APPOINTMENT_TYPE_MAPPING[patientType];
    if (!mapping || !mapping[duration]) {
      console.warn(`No mapping found for ${patientType} patient with ${duration} minute duration`);
      return this.findClosestMatch(duration, patientType, filteredTypes);
    }

    const durationMapping = mapping[duration];

    // Try preferred types first (only from filtered types)
    if (durationMapping.preferred) {
      const preferredTypes = Array.isArray(durationMapping.preferred) 
        ? durationMapping.preferred 
        : [durationMapping.preferred];

      for (const preferredName of preferredTypes) {
        const match = filteredTypes.find(type => 
          type.name === preferredName && type.duration === duration
        );
        if (match) {
          console.log(`✅ Found preferred match: ${match.name} (ID: ${match.appointmenttypeid}) for ${patientType} patient, ${duration}min`);
          return match;
        }
      }
    }

    // Try fallback type (only from filtered types)
    if (durationMapping.fallback) {
      const fallbackTypes = Array.isArray(durationMapping.fallback) 
        ? durationMapping.fallback 
        : [durationMapping.fallback];
        
      for (const fallbackName of fallbackTypes) {
        const fallbackMatch = filteredTypes.find(type => 
          type.name === fallbackName && type.duration === duration
        );
        if (fallbackMatch) {
          console.log(`✅ Using fallback match: ${fallbackMatch.name} (ID: ${fallbackMatch.appointmenttypeid}) for ${patientType} patient, ${duration}min`);
          return fallbackMatch;
        }
      }
    }

    // Last resort: find closest match from filtered types
    return this.findClosestMatch(duration, patientType, filteredTypes);
  }

  /**
   * Find the closest appointment type by duration when exact matches aren't available
   * @param {number} targetDuration - Target duration in minutes
   * @param {string} patientType - 'new' or 'existing'
   * @param {Array} appointmentTypes - Available appointment types
   * @returns {Object} Closest matching appointment type
   */
  static findClosestMatch(targetDuration, patientType, appointmentTypes) {
    // Filter appointment types by patient type first
    let candidateTypes = appointmentTypes.filter(type => 
      this.matchesPatientType(type.name, patientType)
    );

    // If no patient-specific types found, use ANY types
    if (candidateTypes.length === 0) {
      candidateTypes = appointmentTypes.filter(type => 
        type.name.toLowerCase().includes('any')
      );
    }

    // If still no matches, use all types
    if (candidateTypes.length === 0) {
      candidateTypes = appointmentTypes;
    }

    // Find the type with duration closest to target
    let closestMatch = candidateTypes[0];
    let smallestDifference = Math.abs(closestMatch.duration - targetDuration);

    for (const type of candidateTypes) {
      const difference = Math.abs(type.duration - targetDuration);
      if (difference < smallestDifference) {
        smallestDifference = difference;
        closestMatch = type;
      }
    }

    console.log(`Using closest match: ${closestMatch.name} (ID: ${closestMatch.appointmenttypeid}, ${closestMatch.duration}min) for ${patientType} patient, target ${targetDuration}min`);
    return closestMatch;
  }

  /**
   * Check if an appointment type name matches the patient type
   * @param {string} appointmentTypeName - Name of the appointment type
   * @param {string} patientType - 'new' or 'existing'
   * @returns {boolean} True if it matches
   */
  static matchesPatientType(appointmentTypeName, patientType) {
    if (!appointmentTypeName) return false;
    
    const typeName = appointmentTypeName.toLowerCase();
    
    if (patientType === 'new') {
      return typeName.includes('new patient') || typeName.includes('new');
    }
    
    if (patientType === 'existing') {
      return typeName.includes('follow up') || 
             typeName.includes('follow-up') ||
             typeName.includes('telehealth');
    }
    
    return false;
  }

  /**
   * Map a slot to the appropriate appointment type
   * @param {Object} slot - The appointment slot
   * @param {string} patientType - 'new' or 'existing'
   * @param {Array} appointmentTypes - Available appointment types
   * @returns {Object} Enhanced slot with correct appointment type info
   */
  static mapSlotToAppointmentType(slot, patientType, appointmentTypes) {
    if (!slot || !appointmentTypes) {
      console.warn('Invalid slot or appointment types for mapping');
      return slot;
    }

    console.log(`🔄 Mapping slot: ${slot.patientappointmenttypename} (ID: ${slot.appointmenttypeid}) with ${slot.duration}min for ${patientType} patient`);

    // Find the best appointment type for this slot
    const bestMatch = this.findBestMatch(slot.duration, patientType, appointmentTypes);
    
    if (!bestMatch) {
      console.warn('⚠️ No appointment type match found for slot:', {
        original: slot.patientappointmenttypename,
        duration: slot.duration,
        patientType
      });
      return slot;
    }

    if (bestMatch.appointmenttypeid !== slot.appointmenttypeid) {
      console.log(`✅ Mapping: ${slot.patientappointmenttypename} (ID: ${slot.appointmenttypeid}) → ${bestMatch.name} (ID: ${bestMatch.appointmenttypeid})`);
    } else {
      console.log(`ℹ️ No change needed: ${slot.patientappointmenttypename} (ID: ${slot.appointmenttypeid}) already correct`);
    }

    // Return enhanced slot with correct appointment type info
    return {
      ...slot,
      // Update with correct appointment type info
      appointmenttypeid: bestMatch.appointmenttypeid,
      appointmenttype: bestMatch.name,
      patientappointmenttypename: bestMatch.patientdisplayname || bestMatch.name,
      // Keep original for reference/debugging
      originalAppointmentTypeId: slot.appointmenttypeid,
      originalAppointmentType: slot.appointmenttype,
      // Add mapping info for debugging
      mappingInfo: {
        originalDuration: slot.duration,
        mappedType: bestMatch.name,
        mappedId: bestMatch.appointmenttypeid,
        patientType: patientType
      }
    };
  }

  /**
   * Process multiple slots to map them to appropriate appointment types
   * @param {Array} slots - Array of appointment slots
   * @param {string} patientType - 'new' or 'existing'
   * @param {Array} appointmentTypes - Available appointment types
   * @returns {Array} Array of enhanced slots
   */
  static mapSlotsToAppointmentTypes(slots, patientType, appointmentTypes) {
    if (!slots || !Array.isArray(slots)) {
      console.warn('Invalid slots array for mapping');
      return slots || [];
    }

    console.log(`Mapping ${slots.length} slots for ${patientType} patient type`);
    
    const mappedSlots = slots.map(slot => 
      this.mapSlotToAppointmentType(slot, patientType, appointmentTypes)
    );

    // Log mapping summary
    const mappingSummary = mappedSlots.reduce((acc, slot) => {
      const key = `${slot.appointmenttype} (${slot.duration}min)`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    console.log('Slot mapping summary:', mappingSummary);
    
    return mappedSlots;
  }
}

export default AppointmentTypeMapper;
